import { DownloadIcon } from "@chakra-ui/icons";
import { Button, Icon, Link } from "@chakra-ui/react";
import { ColumnDef, Row } from "@tanstack/table-core";
import { useCallback, useMemo } from "react";
import { RiUserSettingsLine } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { BannerSelection } from "src/components/Table/BannerSelection";
import { PaginatedTable } from "src/components/Table/PaginatedTable";
import { SortType } from "src/components/Table/SortButton";
import { ELEVATED_SCHOOL_ADMIN_GROUP_NAME } from "src/constants";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { useTableSelection } from "src/hooks/useTableSelection";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { csvExport } from "src/services/dataTransfer";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { BulkArchiveTeamMembersButton } from "./BulkArchiveTeamMembersButton";

interface MemberListProps {
  active: boolean;
  remoteData: RD.RemoteData<unknown, GQL.GetMembers>;
  limit: number;
  offset: number;
  onFetchMore: (limit: number, offset: number) => void;
  onSort: (columnName: string, sortType: SortType) => void;
  onFetchAll: () => Promise<GQL.GetMembers_person[]>;
  refetch: () => Promise<unknown>;
  onClearSearch: () => void;
}

const toSchoolList = (items: GQL.GetMembers_person): string =>
  items.user?.school_users.map((x) => x?.school?.name || "").join(", ") || "";

export const MemberList: React.FC<MemberListProps> = ({
  active,
  remoteData,
  limit,
  offset,
  onFetchMore,
  onSort,
  onFetchAll,
  onClearSearch,
  refetch,
}) => {
  const { members, count } = useMemo(() => {
    const data = remoteData.toNullable();
    return {
      members: data?.person ?? [],
      count: data?.person_aggregate.totals?.total ?? 0,
    };
  }, [remoteData]);
  const { glossary } = useGlossary();
  const organization = useOrganization();
  const userPermissions = useUserPermissions();
  const canEdit =
    useRequiredHasuraRoles([HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]) &&
    userPermissions.hasOne("user:update");

  const { selection, setSelection, checkboxColumnDef } = useTableSelection({
    getId: (member: GQL.GetMembers_person) => member.id,
    count,
  });

  const getSchools = (row: GQL.GetMembers_person): string => {
    return row.person_type === GQL.person_type_enum.schoolAdmin ||
      row.user_group?.name === ELEVATED_SCHOOL_ADMIN_GROUP_NAME
      ? toSchoolList(row)
      : "All";
  };

  const getCurrentApplyingDesignation = (
    row: GQL.GetMembers_person
  ): string => {
    // Currently, the same current/applying designation applies to all schools,
    // although the data attaches permissions per school.
    // For now, we'll just assume the first school's designation is the current/applying designation.
    const currentDesignation = row.user?.school_users
      .map((x) => x.school_access_permission)[0]
      ?.split(":")[1];
    switch (currentDesignation) {
      case "applying":
        return "Applying";
      case "attending":
        return "Current";
      case "all":
        return "All";
      default:
        return "";
    }
  };

  const columns: ColumnDef<GQL.GetMembers_person>[] = [
    checkboxColumnDef({ id: "Selection" }),
    {
      id: "id",
      accessorFn: (row) => row.id || "",
    },
    {
      id: "team_member",
      header: "team member",
      cell: (props) => createRowLink(props.row, props.row.original.full_name),
    },
    {
      id: "email_address",
      header: "email address",
      cell: (props) =>
        createRowLink(props.row, props.row.original.email_address),
    },
    {
      id: "role",
      header: "role",
      accessorFn: (row) => row.user_group?.label,
    },
    {
      id: "team",
      header: "team",
      accessorFn: (row) => row.person_teams[0]?.team.name,
    },
    {
      id: "schools",
      header: glossary`schools`,
      accessorFn: getSchools,
    },
    {
      id: "current_applying_designation",
      header: "current/applying designation",
      accessorFn: getCurrentApplyingDesignation,
    },
  ];

  const handleChangeSort = useCallback(
    (headerId: string, sortType: SortType) => {
      onSort(headerId, sortType);
    },
    [onSort]
  );

  function createRowLink(
    row: Row<GQL.GetMembers_person>,
    text: string | null,
    emptyText?: string
  ) {
    return canEdit ? (
      <Link
        {...(text
          ? { color: "primary.500", textDecoration: "underline" }
          : {
              opacity: "36%",
              fontStyle: "italic",
            })}
        as={RouterLink}
        to={organization
          .map((org) => Url.OrgAdmin.Team.editMember(org, row.original.id))
          .withDefault("#")}
      >
        {text ?? emptyText ?? "None"}
      </Link>
    ) : (
      text ?? ""
    );
  }

  const clearSelection = useCallback(
    () => setSelection(selection.clear()),
    [selection, setSelection]
  );

  if (remoteData.hasError()) {
    return <GenericError />;
  }

  if (!remoteData.isLoading() && count === 0) {
    return (
      <NoDataTable
        icon={RiUserSettingsLine}
        text="No members found"
        buttonText="Clear search"
        onButtonAction={onClearSearch}
      />
    );
  }

  return (
    <PaginatedTable<GQL.GetMembers_person>
      isLoading={remoteData.isLoading()}
      data={members}
      columns={columns}
      limit={limit}
      offset={offset}
      count={count}
      onFetchMore={onFetchMore}
      sortableColumnIds={["role", "email_address"]}
      onChangeSort={handleChangeSort}
      banner={() => (
        <BannerSelection selection={selection}>
          <BulkArchiveTeamMembersButton
            active={active}
            selection={selection}
            clearSelection={clearSelection}
            onFetchAll={onFetchAll}
            refetch={refetch}
          />
          <Button
            variant="ghost"
            colorScheme="gray"
            size="sm"
            leftIcon={<Icon as={DownloadIcon} />}
            onClick={async () => {
              const records = await selection.materialize(onFetchAll);

              csvExport(
                records.map((row) => {
                  const {
                    id,
                    first_name,
                    last_name,
                    email_address,
                    person_teams,
                    user_group,
                  } = row;
                  return {
                    ID: id || "",
                    "Team Member Name":
                      (first_name ?? "") + " " + (last_name ?? ""),
                    "Email Address": email_address ?? "",
                    Role: user_group?.label ?? "",
                    Team: person_teams[0]?.team.name ?? "",
                    Schools: getSchools(row),
                    "Current/Applying Designation":
                      getCurrentApplyingDesignation(row),
                  };
                }),
                "members.csv"
              );
            }}
          >
            Export
          </Button>
        </BannerSelection>
      )}
    />
  );
};
